/**
 * Детерминированный визуал категории.
 *
 * Категория не хранит картинку: цвет и глиф выводятся из имени. Одинаковое
 * имя всегда даёт одинаковый визуал на всех устройствах, без сети и файлов.
 */

export interface CategoryGradient {
  /** Начальный цвет градиента. */
  from: string
  /** Конечный цвет градиента. */
  to: string
}

/** Число hue-семей: 8 значений с шагом 45°. */
export const CATEGORY_HUE_COUNT = 8

/**
 * 32 цвета: 24 аналоговых градиента + 8 однотонных fallback.
 *
 * Градиенты собраны из соседних оттенков цветового круга (красный→малина,
 * оранжевый→терракота, янтарь→олива, лайм→зелёный, зелёный→изумруд,
 * изумруд→бирюза, бирюза→голубой, голубой→синий, синий→индиго,
 * индиго→фиолет, фиолет→пурпур, пурпур→роза). Это даёт заметный переход
 * на плитке товара, а не «один цвет с тенью».
 *
 * Все стопы средней светлоты (L ≈ 0.45–0.55): белая цифра проходит
 * WCAG 1.4.11 (3:1) на светлой и тёмной теме. Проверка — в
 * `categoryVisual.test.ts`.
 *
 * Последние 8 — однотонные fallback для будущих категорий, если градиентов
 * не хватит. Семантические `#DC2626`/`#16A34A` не используются, чтобы бейджи
 * не путались с категориями.
 *
 * Обоснование: `docs/UX-CATEGORY-PALETTE.md`.
 */
export const CATEGORY_PALETTE: CategoryGradient[] = [
  // 24 аналоговых градиента: from светлее, to заметно темнее
  { from: '#B84A4A', to: '#8A1F3A' }, // red → raspberry
  { from: '#B85E32', to: '#8A2A1F' }, // orange → terracotta
  { from: '#A87A28', to: '#7A4A14' }, // amber → olive
  { from: '#7F942E', to: '#4F6A14' }, // lime → green
  { from: '#3F8F5A', to: '#1F6A3A' }, // green → emerald
  { from: '#2A8F7F', to: '#146A5A' }, // emerald → teal
  { from: '#2A7F9A', to: '#145A7A' }, // teal → sky
  { from: '#2F6FA8', to: '#1A4A7A' }, // sky → blue
  { from: '#4A5FA8', to: '#2A3A7A' }, // blue → indigo
  { from: '#6A4AA8', to: '#4A2A7A' }, // indigo → violet
  { from: '#8A3FA8', to: '#6A1F7A' }, // violet → purple
  { from: '#A83A8A', to: '#7A1F5F' }, // purple → rose
  { from: '#A83A6A', to: '#7A1F3A' }, // rose → raspberry
  { from: '#A84A4A', to: '#7A1F1F' }, // raspberry → red
  { from: '#A86A32', to: '#7A3A14' }, // peach → orange
  { from: '#8A8A2A', to: '#5F5F14' }, // gold → olive
  { from: '#5A9A3A', to: '#2F6A1F' }, // green → lime
  { from: '#2A9A7A', to: '#146A4F' }, // teal → emerald
  { from: '#2A7A9A', to: '#145A6A' },
  { from: '#3A5A8A', to: '#1F2F6A' },
  { from: '#5A3A9A', to: '#2F1F6A' },
  { from: '#7A2A9A', to: '#4F146A' },
  { from: '#9A2A7A', to: '#6A1450' },
  { from: '#9A3A5A', to: '#6A1F3A' },
  // 8 однотонных fallback
  { from: '#8A3A3A', to: '#8A3A3A' },
  { from: '#8A5A2A', to: '#8A5A2A' },
  { from: '#6F6F1F', to: '#6F6F1F' },
  { from: '#3F7A3A', to: '#3F7A3A' },
  { from: '#2A7A6A', to: '#2A7A6A' },
  { from: '#2A5F7A', to: '#2A5F7A' },
  { from: '#4A3A7A', to: '#4A3A7A' },
  { from: '#7A2A5A', to: '#7A2A5A' }
]

/** Количество минималистичных глифов в наборе. */
export const CATEGORY_GLYPH_COUNT = 16

export interface CategoryVisual {
  /** Индекс градиента в палитре. */
  colorKey: number
  /** Индекс глифа. */
  glyph: number
}

/** FNV-1a: стабильный 32-битный хеш строки. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * Визуал категории. Ручные `colorKey`/`glyph` имеют приоритет над хешем.
 *
 * `parent` — имя верхней категории (отдела). Если задано, цвет берётся из
 * отдела, а иконка — из листа. Так соседи внутри отдела получают один цвет
 * (визуальная группировка), а подкатегории различаются иконкой. Без `parent`
 * цвет считается по имени — обратная совместимость.
 */
export function categoryVisual(
  name: string,
  colorKey?: number | null,
  glyph?: number | null,
  parent?: string | null
): CategoryVisual {
  const leafHash = hashString(name.trim().toLowerCase())
  const colorSource = parent?.trim()
    ? parent.trim().toLowerCase()
    : name.trim().toLowerCase()
  const colorHash = hashString(colorSource)

  return {
    colorKey:
      colorKey != null && colorKey >= 0
        ? colorKey % CATEGORY_PALETTE.length
        : colorHash % CATEGORY_PALETTE.length,
    glyph:
      glyph != null && glyph >= 0
        ? glyph % CATEGORY_GLYPH_COUNT
        : Math.floor(leafHash / CATEGORY_PALETTE.length) % CATEGORY_GLYPH_COUNT
  }
}

/** Градиент категории (для плиток товаров и фонов). */
export function categoryGradient(
  name: string,
  colorKey?: number | null,
  parent?: string | null
): CategoryGradient {
  return CATEGORY_PALETTE[categoryVisual(name, colorKey, null, parent).colorKey]
}
